import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Copy,
  Loader2,
  RefreshCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { AiFeedback, AiMessage, ChatUiMessage } from '@/types/ai';
import { sanitizeUserMessageContent } from '@/lib/aiMessage';
import { renderChatMarkdown } from '@/components/chat/markdown/renderChatMarkdown';
import { ChatFileAttachment } from '@/components/chat/ChatFileAttachment';
import { ChatWidgets } from '@/components/chat/ChatWidgets';
import { FollowUpChips } from '@/components/chat/FollowUpChips';
import { isWizardPrompt, WizardPromptCard } from '@/components/chat/WizardPromptCard';

interface ChatMessageProps {
  message: AiMessage | ChatUiMessage;
  conversationId?: string;
  onRetry?: (message: ChatUiMessage) => void;
  onConfirm?: (text: string) => void;
  onCancel?: (text: string) => void;
  onFeedback?: (messageId: string, feedback: NonNullable<AiFeedback>) => void;
  onSelectFollowUp?: (prompt: string) => void;
  isLastAssistant?: boolean;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isChatUiMessage(message: AiMessage | ChatUiMessage): message is ChatUiMessage {
  return 'status' in message;
}

function isConfirmationRequest(content: string): boolean {
  const lower = content.toLowerCase();
  return lower.includes('confirmer') && lower.includes('annuler');
}

export function ChatMessage({
  message,
  conversationId,
  onRetry,
  onConfirm,
  onCancel,
  onFeedback,
  onSelectFollowUp,
  isLastAssistant,
}: ChatMessageProps) {
  const { t } = useTranslation('dashboard');
  const [actionTaken, setActionTaken] = useState(false);
  const [localFeedback, setLocalFeedback] = useState<AiFeedback>(message.feedback ?? null);
  const isUser = message.role === 'user';
  const status = isChatUiMessage(message) ? message.status : 'done';
  const showConfirmation =
    !isUser && status === 'done' && isConfirmationRequest(message.content) && !actionTaken;
  const isFailed = status === 'failed';
  const isPending = status === 'pending';
  const isStreaming = status === 'streaming';
  const hasFiles = Array.isArray(message.files) && message.files.length > 0;
  const sanitizedUserContent = isUser ? sanitizeUserMessageContent(message.content) : '';
  const displayContent = isUser
    ? sanitizedUserContent || t('chat_voice_command')
    : message.content.trim() === ''
      ? isStreaming
        ? ''
        : hasFiles
          ? 'Document généré. Utilisez les actions ci-dessous.'
          : 'Réponse indisponible. Réessayez.'
      : message.content;

  function handleCopy() {
    if (!message.content.trim()) return;
    if (!navigator?.clipboard?.writeText) {
      toast.error(t('chat_copy_failed'));
      return;
    }
    navigator.clipboard
      .writeText(message.content)
      .then(() => toast.success(t('chat_copied')))
      .catch(() => toast.error(t('chat_copy_failed')));
  }

  if (isUser) {
    return (
      <div className="group flex w-full justify-end">
        <div className="flex max-w-[80%] flex-col items-end gap-1">
          <div
            className={cn(
              'rounded-2xl bg-charcoal-100 dark:bg-charcoal-800 px-4 py-2.5 text-sm text-charcoal-900 dark:text-charcoal-50',
              isPending && 'opacity-70',
              isFailed && 'border-l-2 border-error',
            )}
          >
            {isWizardPrompt(displayContent) ? (
              <WizardPromptCard content={displayContent} />
            ) : (
              <p className="whitespace-pre-wrap break-words">{displayContent}</p>
            )}
          </div>
          <span className="text-xs text-muted-foreground px-1">
            {formatTime(message.created_at)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex w-full">
      <div className={cn('flex-1 min-w-0', isFailed && 'border-l-2 border-error pl-3')}>
        <div className="flex items-start gap-2.5">
          <Sparkles className="mt-0.5 size-5 shrink-0 text-primary" />
          <div className="flex-1 min-w-0">
            <article className="chat-markdown break-words text-sm text-charcoal-900 dark:text-charcoal-50">
              {renderChatMarkdown(displayContent)}
              {isStreaming && displayContent !== '' && (
                <span className="ml-1 inline-block animate-pulse">▋</span>
              )}
            </article>

            {isStreaming && displayContent === '' && (
              <span className="inline-flex items-center gap-1 py-1">
                <span className="size-1.5 rounded-full bg-charcoal-400 animate-pulse-dot" />
                <span
                  className="size-1.5 rounded-full bg-charcoal-400 animate-pulse-dot"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="size-1.5 rounded-full bg-charcoal-400 animate-pulse-dot"
                  style={{ animationDelay: '300ms' }}
                />
              </span>
            )}

            {hasFiles && (
              <div className="mt-2 flex flex-col gap-2">
                {message.files?.map((file) => (
                  <ChatFileAttachment key={file.file_url} file={file} />
                ))}
              </div>
            )}

            {!isUser && (
              <ChatWidgets
                widgets={message.widgets}
                conversationId={conversationId}
                messageId={message.id}
              />
            )}

            {isPending && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] opacity-80">
                <Loader2 className="size-3 animate-spin" />
                {t('chat_status_sending')}
              </p>
            )}

            {isFailed && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-error-dark">
                <TriangleAlert className="size-3" />
                {t('chat_send_failed')}
              </p>
            )}

            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {formatTime(message.created_at)}
              </span>
              {!isUser && message.credits_consumed != null && message.credits_consumed > 1 && (
                <span className="text-xs text-amber-500 font-medium">
                  {message.credits_consumed} crédits
                </span>
              )}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted/80 transition-colors"
                >
                  <Copy className="size-3" />
                  {t('chat_copy')}
                </button>
                {status === 'done' && onFeedback && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setLocalFeedback('positive');
                        onFeedback(message.id, 'positive');
                      }}
                      className={cn(
                        'inline-flex items-center rounded-md p-1 transition-colors',
                        localFeedback === 'positive'
                          ? 'text-green-600'
                          : 'text-muted-foreground hover:text-green-600 hover:bg-muted/80',
                      )}
                      aria-label={t('chat_feedback_positive', { defaultValue: 'Bonne réponse' })}
                    >
                      <ThumbsUp className="size-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLocalFeedback('negative');
                        onFeedback(message.id, 'negative');
                      }}
                      className={cn(
                        'inline-flex items-center rounded-md p-1 transition-colors',
                        localFeedback === 'negative'
                          ? 'text-red-500'
                          : 'text-muted-foreground hover:text-red-500 hover:bg-muted/80',
                      )}
                      aria-label={t('chat_feedback_negative', { defaultValue: 'Mauvaise réponse' })}
                    >
                      <ThumbsDown className="size-3" />
                    </button>
                  </>
                )}
              </div>
              {isFailed && isChatUiMessage(message) && onRetry && (
                <button
                  type="button"
                  onClick={() => onRetry(message)}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-error-dark hover:bg-error-light transition-colors"
                >
                  <RefreshCw className="size-3" />
                  {t('chat_retry')}
                </button>
              )}
            </div>

            {showConfirmation && (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    onConfirm?.('Confirmer');
                    setActionTaken(true);
                  }}
                >
                  {t('chat_confirm', { defaultValue: 'Confirmer' })}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onCancel?.('Annuler');
                    setActionTaken(true);
                  }}
                >
                  {t('chat_cancel', { defaultValue: 'Annuler' })}
                </Button>
              </div>
            )}

            {isLastAssistant && status === 'done' && onSelectFollowUp && (
              <FollowUpChips
                toolCalls={message.tool_calls}
                content={message.content}
                onSelect={onSelectFollowUp}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
